/**
 * Settings Modal UI Component
 * Handles user preferences and account management
 */

import { i18nService, loadUIStrings } from '../services/i18n-service.js';
import { authService } from '../services/auth-service.js';
import appState from '../core/app-state.js';
import { createSVGIcon } from '../core/router.js';
import logger from '../core/logger.js';

/**
 * Callback types for settings modal dependencies
 */
interface SettingsCallbacks {
  showToast?: (message: string, type?: string, duration?: number) => void;
  showConfirmModal?: (message: string, onConfirm?: () => void, onCancel?: () => void) => void;
  updateFooter?: () => void;
  renderLibraryView?: () => void;
  renderHomeView?: () => void;
  generateDailySet?: (mode: string) => void;
  getTodayDate?: () => string;
}

// Dependencies passed from main.js
let showToast: ((message: string, type?: string, duration?: number) => void) | null = null;
let showConfirmModal: ((message: string, onConfirm?: () => void, onCancel?: () => void) => void) | null = null;
let updateFooter: (() => void) | null = null;
let renderLibraryView: (() => void) | null = null;
let renderHomeView: (() => void) | null = null;
let generateDailySet: ((mode: string) => void) | null = null;
let getTodayDate: (() => string) | null = null;

/**
 * Register external dependencies
 */
export function registerSettingsCallbacks(callbacks: SettingsCallbacks): void {
  showToast = callbacks.showToast ?? null;
  showConfirmModal = callbacks.showConfirmModal ?? null;
  updateFooter = callbacks.updateFooter ?? null;
  renderLibraryView = callbacks.renderLibraryView ?? null;
  renderHomeView = callbacks.renderHomeView ?? null;
  generateDailySet = callbacks.generateDailySet ?? null;
  getTodayDate = callbacks.getTodayDate ?? null;
}

/**
 * Get list of unique languages from clips data
 */
function getAvailableLanguages(): string[] {
  if (!appState.clips || appState.clips.length === 0) {
    return ['tr', 'de']; // Default fallback: Turkish and German
  }
  
  const languages = new Set<string>();
  appState.clips.forEach(clip => {
    if (clip.language) {
      languages.add(clip.language);
    }
  });
  
  // Always include Turkish and German as supported languages
  languages.add('tr');
  languages.add('de');
  
  return Array.from(languages).sort();
}

/**
 * Get language display names
 */
function getLanguageDisplayName(langCode: string): string {
  const names: Record<string, string> = {
    'tr': 'Türkçe (Turkish)',
    'de': 'Deutsch (German)',
    'en': 'English',
    'es': 'Español (Spanish)',
    'fr': 'Français (French)',
    'it': 'Italiano (Italian)'
  };
  return names[langCode] || langCode.toUpperCase();
}

/**
 * Check if clip is completed
 */
function isClipCompleted(clipId: string): boolean {
  return !!appState.progressMap[clipId];
}

/**
 * Calculate average clip duration for time estimates (fallback: 3.5 minutes)
 */
function calculateAverageClipDuration(): number {
  if (!appState.clips || appState.clips.length === 0) {
    return 3.5; // fallback: 3-4 min per clip
  }
  
  // Try to calculate from completed clips first for better accuracy
  const completedClips = appState.clips.filter(clip => isClipCompleted(clip.id));
  const sampleClips = completedClips.length >= 5 ? completedClips : appState.clips;
  
  // Calculate average from sentences if available
  let totalDuration = 0;
  let validCount = 0;
  
  for (const clip of sampleClips.slice(0, 20)) { // Sample up to 20 clips
    if (clip.sentences && clip.sentences.length > 0) {
      const lastSentence = clip.sentences[clip.sentences.length - 1];
      if (lastSentence && lastSentence.end) {
        totalDuration += lastSentence.end / 60; // Convert to minutes
        validCount++;
      } else if (lastSentence && lastSentence.start) {
        // Fallback: estimate 3s per sentence and add to last start
        totalDuration += (lastSentence.start + 3) / 60;
        validCount++;
      }
    }
  }
  
  return validCount > 0 ? totalDuration / validCount : 3.5;
}

/**
 * Extended HTMLElement with escHandler property
 */
interface ModalOverlay extends HTMLElement {
  escHandler?: (e: KeyboardEvent) => void;
}

/**
 * Close settings modal
 */
function closeSettingsModal(): void {
  const modalContainer = document.getElementById('modal-container');
  if (!modalContainer) return;
  
  // Remove ESC key listener if it exists
  const overlay = modalContainer.querySelector('.modal-overlay') as ModalOverlay | null;
  if (overlay && overlay.escHandler) {
    document.removeEventListener('keydown', overlay.escHandler);
  }
  
  modalContainer.innerHTML = '';
}

/**
 * Save user preferences to localStorage
 */
export function savePreferences(): void {
  try {
    localStorage.setItem('listen-up-preferences', JSON.stringify(appState.preferences));
  } catch (e) {
    logger.error('Failed to save preferences', e);
  }
}

/**
 * Load user preferences from localStorage
 */
export function loadPreferences(): void {
  try {
    const prefsString = localStorage.getItem('listen-up-preferences');
    if (prefsString) {
      const loadedPrefs = JSON.parse(prefsString);
      // Update global reference
      appState.preferences = loadedPrefs;
      
      // Set language from preferences
      if (loadedPrefs.uiLanguage) {
        i18nService.setLanguage(loadedPrefs.uiLanguage);
      }
      
      // Ensure cefrLevel has a default if not set
      if (!appState.preferences['cefrLevel']) {
        appState.preferences['cefrLevel'] = 'B2';
      }
    } else {
      // Default preferences
      const defaultPrefs: Partial<Record<string, unknown>> = {
        filterLevel: 'all', // default: show all difficulty levels
        cefrLevel: 'B2', // default CEFR level (set after onboarding or defaulted)
        uiLanguage: 'en', // default language
        dailyGoal: 1, // default daily goal (1-5 appState.clips)
        learningLanguage: 'tr' // default learning language (Turkish)
      };
      appState.preferences = defaultPrefs;
      i18nService.setLanguage('en');
      localStorage.setItem('listen-up-preferences', JSON.stringify(defaultPrefs));
    }
    
    // Migrate existing preferences to include dailyGoal if missing
    if (!appState.preferences['dailyGoal']) {
      appState.preferences['dailyGoal'] = 1;
      savePreferences();
    }
    
    // Migrate existing preferences to include learningLanguage if missing
    if (!appState.preferences['learningLanguage']) {
      const availableLanguages = getAvailableLanguages();
      appState.preferences['learningLanguage'] = availableLanguages.length > 0 ? availableLanguages[0] : 'tr';
      savePreferences();
    }
  } catch (e) {
    logger.error('Failed to load preferences', e);
    const fallbackPrefs: Partial<Record<string, unknown>> = { 
      filterLevel: 'all', 
      cefrLevel: 'B2', 
      uiLanguage: 'en', 
      dailyGoal: 1, 
      learningLanguage: 'tr' 
    };
    appState.preferences = fallbackPrefs;
    i18nService.setLanguage('en');
  }
}

/**
 * Render settings modal
 */
export function renderSettingsModal(): void {
  const modalContainer = document.getElementById('modal-container');
  if (!modalContainer) return;
  
  // Create modal overlay
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-labelledby', 'settings-modal-title');
  
  // Create modal panel
  const panel = document.createElement('div');
  panel.className = 'modal-panel';
  
  // Modal header
  const header = document.createElement('div');
  header.className = 'modal-header';
  
  const title = document.createElement('h2');
  title.id = 'settings-modal-title';
  title.className = 'modal-title';
  title.textContent = i18nService.getString('settings') || 'Settings';
  header.appendChild(title);
  
  const closeBtn = document.createElement('button');
  closeBtn.className = 'modal-close-btn';
  closeBtn.setAttribute('aria-label', 'Close settings');
  
  // Use helper function for X icon
  const closeIcon = createSVGIcon('x-mark', 'icon');
  closeIcon.style.cssText = 'width: 1.5rem; height: 1.5rem;';
  closeBtn.appendChild(closeIcon);
  
  closeBtn.addEventListener('click', closeSettingsModal);
  header.appendChild(closeBtn);
  
  panel.appendChild(header);
  
  // Modal body
  const body = document.createElement('div');
  body.className = 'modal-body';
  
  // General section
  const generalSection = document.createElement('div');
  generalSection.className = 'modal-section';
  
  const generalTitle = document.createElement('h3');
  generalTitle.className = 'modal-section-title';
  generalTitle.textContent = i18nService.getString('general') || 'General';
  generalSection.appendChild(generalTitle);
  
  // Language select
  const langField = document.createElement('div');
  langField.className = 'modal-field';
  
  const langLabel = document.createElement('label');
  langLabel.className = 'modal-label';
  langLabel.textContent = i18nService.getString('language') || 'Language';
  langLabel.setAttribute('for', 'settings-language');
  langField.appendChild(langLabel);
  
  const langSelect = document.createElement('select');
  langSelect.id = 'settings-language';
  langSelect.className = 'modal-select';
  langSelect.innerHTML = '<option value="en">English</option><option value="tr">Türkçe</option><option value="de">Deutsch</option>';
  langSelect.value = String(appState.preferences['uiLanguage'] || 'en');
  langField.appendChild(langSelect);
  
  generalSection.appendChild(langField);
  
  // Level select
  const levelField = document.createElement('div');
  levelField.className = 'modal-field';
  
  const levelLabel = document.createElement('label');
  levelLabel.className = 'modal-label';
  levelLabel.textContent = i18nService.getString('level') || 'Level';
  levelLabel.setAttribute('for', 'settings-level');
  levelField.appendChild(levelLabel);
  
  const levelSelect = document.createElement('select');
  levelSelect.id = 'settings-level';
  levelSelect.className = 'modal-select';
  levelSelect.innerHTML = '<option value="A2">A2</option><option value="B1">B1</option><option value="B2">B2</option>';
  levelSelect.value = String(appState.preferences['cefrLevel'] || 'B2');
  levelField.appendChild(levelSelect);
  
  generalSection.appendChild(levelField);
  
  // Learning Language select
  const learningLangField = document.createElement('div');
  learningLangField.className = 'modal-field';
  
  const learningLangLabel = document.createElement('label');
  learningLangLabel.className = 'modal-label';
  learningLangLabel.textContent = i18nService.getString('learningLanguage') || 'Learning Language';
  learningLangLabel.setAttribute('for', 'settings-learning-language');
  learningLangField.appendChild(learningLangLabel);
  
  const learningLangSelect = document.createElement('select');
  learningLangSelect.id = 'settings-learning-language';
  learningLangSelect.className = 'modal-select';
  
  // Get available languages from clips
  const availableLanguages = getAvailableLanguages();
  
  // Add "All Languages" option (for advanced users)
  const allOption = document.createElement('option');
  allOption.value = 'all';
  allOption.textContent = i18nService.getString('allLanguages') || 'All Languages';
  learningLangSelect.appendChild(allOption);
  
  // Add each available language
  availableLanguages.forEach(lang => {
    const option = document.createElement('option');
    option.value = lang;
    option.textContent = getLanguageDisplayName(lang);
    learningLangSelect.appendChild(option);
  });
  
  // Set current value
  learningLangSelect.value = String(appState.preferences['learningLanguage'] || 'tr');
  learningLangField.appendChild(learningLangSelect);
  
  // Add help text
  const learningLangHelp = document.createElement('div');
  learningLangHelp.className = 'modal-field-helper';
  learningLangHelp.style.cssText = 'font-size: 0.875rem; color: #6b7280; margin-top: 0.25rem;';
  learningLangHelp.textContent = i18nService.getString('learningLanguageHelp') || 'Choose which language you want to learn. Only clips in this language will be shown.';
  learningLangField.appendChild(learningLangHelp);
  
  generalSection.appendChild(learningLangField);
  
  // Daily Goal select with minute estimate
  const dailyGoalField = document.createElement('div');
  dailyGoalField.className = 'modal-field';
  
  const dailyGoalLabel = document.createElement('label');
  dailyGoalLabel.className = 'modal-label';
  dailyGoalLabel.textContent = i18nService.getString('dailyGoal') || 'Daily Goal';
  dailyGoalLabel.setAttribute('for', 'settings-daily-goal');
  dailyGoalField.appendChild(dailyGoalLabel);
  
  const dailyGoalSelect = document.createElement('select');
  dailyGoalSelect.id = 'settings-daily-goal';
  dailyGoalSelect.className = 'modal-select';
  dailyGoalSelect.innerHTML = '<option value="1">1</option><option value="2">2</option><option value="3">3</option><option value="4">4</option><option value="5">5</option>';
  dailyGoalSelect.value = String(appState.preferences['dailyGoal'] || 1);
  dailyGoalField.appendChild(dailyGoalSelect);
  
  // Add minute estimate helper text
  const minuteEstimate = document.createElement('div');
  minuteEstimate.className = 'modal-field-helper';
  minuteEstimate.id = 'daily-goal-estimate';
  minuteEstimate.style.cssText = 'font-size: 0.875rem; color: #6b7280; margin-top: 0.25rem;';
  const avgMinutes = calculateAverageClipDuration();
  const currentGoal = Number(appState.preferences['dailyGoal']) || 1;
  const estimatedMinutes = Math.round(avgMinutes * currentGoal);
  minuteEstimate.textContent = (i18nService.getString('approxMinutes') || '≈ {minutes} min').replace('{minutes}', String(estimatedMinutes));
  dailyGoalField.appendChild(minuteEstimate);
  
  // Update estimate when selection changes
  dailyGoalSelect.addEventListener('change', (): void => {
    const newGoal = parseInt(dailyGoalSelect.value);
    const newEstimate = Math.round(avgMinutes * newGoal);
    minuteEstimate.textContent = (i18nService.getString('approxMinutes') || '≈ {minutes} min').replace('{minutes}', String(newEstimate));
  });
  
  generalSection.appendChild(dailyGoalField);
  
  body.appendChild(generalSection);
  
  // Account section
  const accountSection = document.createElement('div');
  accountSection.className = 'modal-section';
  
  const accountTitle = document.createElement('h3');
  accountTitle.className = 'modal-section-title';
  accountTitle.textContent = i18nService.getString('account') || 'Account';
  accountSection.appendChild(accountTitle);
  
  // Check if user is signed in
  const currentUser = authService.getCurrentUser();
  
  if (currentUser) {
    // Show signed-in status
    const signedInStatus = document.createElement('div');
    signedInStatus.className = 'modal-signed-in-status';
    signedInStatus.textContent = `${i18nService.getString('signedInAs') || 'Signed in as'}: ${currentUser.email}`;
    accountSection.appendChild(signedInStatus);
    
    // Sign out button
    const signOutBtn = document.createElement('button');
    signOutBtn.className = 'modal-btn modal-btn--secondary';
    signOutBtn.textContent = i18nService.getString('signOut') || 'Sign Out';
    signOutBtn.addEventListener('click', async (): Promise<void> => {
      if (showToast) {
        await authService.signOut(showToast);
      }
      closeSettingsModal();
      renderSettingsModal(); // Re-render to show sign-in form
    });
    accountSection.appendChild(signOutBtn);
  } else {
    // Show email input and sign-in button
    const emailField = document.createElement('div');
    emailField.className = 'modal-field';
    
    const emailLabel = document.createElement('label');
    emailLabel.className = 'modal-label';
    emailLabel.textContent = i18nService.getString('email') || 'Email';
    emailLabel.setAttribute('for', 'settings-email');
    emailField.appendChild(emailLabel);
    
    const emailInput = document.createElement('input');
    emailInput.id = 'settings-email';
    emailInput.type = 'email';
    emailInput.className = 'modal-input';
    emailInput.placeholder = 'you@example.com';
    emailField.appendChild(emailInput);
    
    accountSection.appendChild(emailField);
    
    const signInBtn = document.createElement('button');
    signInBtn.className = 'modal-btn modal-btn--primary';
    signInBtn.textContent = i18nService.getString('sendLink') || 'Send sign-in link';
    signInBtn.addEventListener('click', async (): Promise<void> => {
      const email = emailInput.value.trim();
      if (email && showToast) {
        await authService.signIn(email, showToast);
      }
    });
    accountSection.appendChild(signInBtn);
    
    const infoText = document.createElement('p');
    infoText.className = 'modal-info-text';
    infoText.textContent = i18nService.getString('checkEmail') || 'Check your email for the sign-in link.';
    accountSection.appendChild(infoText);
  }
  
  body.appendChild(accountSection);
  

  
  panel.appendChild(body);
  
  // Modal footer
  const footer = document.createElement('div');
  footer.className = 'modal-footer';
  
  const cancelBtn = document.createElement('button');
  cancelBtn.className = 'modal-btn modal-btn--secondary';
  cancelBtn.textContent = i18nService.getString('cancel') || 'Cancel';
  cancelBtn.addEventListener('click', closeSettingsModal);
  footer.appendChild(cancelBtn);
  
  const saveBtn = document.createElement('button');
  saveBtn.className = 'modal-btn modal-btn--primary';
  saveBtn.textContent = i18nService.getString('save') || 'Save';
  saveBtn.addEventListener('click', (): void => {
    // Save preferences
    const newLang = langSelect.value;
    const newLevel = levelSelect.value;
    const newDailyGoal = parseInt(dailyGoalSelect.value);
    const newLearningLang = learningLangSelect.value;
    
    // Check if daily goal or level changed (to regenerate daily set)
    const oldLevel = String(appState.preferences['cefrLevel']);
    const oldDailyGoal = Number(appState.preferences['dailyGoal']);
    const oldLearningLang = String(appState.preferences['learningLanguage']);
    const levelChanged = newLevel !== oldLevel;
    const dailyGoalChanged = newDailyGoal !== oldDailyGoal;
    const learningLangChanged = newLearningLang !== oldLearningLang;
    
    logger.debug('Settings save - checking for changes', {
      oldLevel,
      newLevel,
      levelChanged,
      oldDailyGoal,
      newDailyGoal,
      dailyGoalChanged,
      oldLearningLang,
      newLearningLang,
      learningLangChanged
    });
    
    // Update preferences - consistent with state management guidelines
    appState.preferences['uiLanguage'] = newLang;
    appState.preferences['cefrLevel'] = newLevel;
    appState.preferences['dailyGoal'] = newDailyGoal;
    appState.preferences['learningLanguage'] = newLearningLang;
    
    savePreferences();
    
    // Helper function to re-render current view
    const reRenderCurrentView = (): void => {
      if (appState.mode === 'library') {
        if (renderLibraryView) {
          renderLibraryView();
        }
      } else if (appState.mode === 'home') {
        if (renderHomeView) {
          renderHomeView();
        }
      }
    };
    
    // Regenerate daily set first if level, daily goal, or learning language changed
    // This must happen before any view re-renders
    if (levelChanged || dailyGoalChanged || learningLangChanged) {
      logger.debug('Settings changed - regenerating daily set', {
        levelChanged,
        dailyGoalChanged,
        learningLangChanged,
        getTodayDateExists: !!getTodayDate,
        generateDailySetExists: !!generateDailySet
      });
      
      if (getTodayDate) {
        const today = getTodayDate();
        logger.debug('Removing localStorage keys for daily set', {
          today,
          newLevel,
          oldLevel
        });
        
        // Remove old daily set for previous level (if level changed)
        if (levelChanged) {
          const oldKey = `dailySet:${today}:${oldLevel}`;
          try {
            localStorage.removeItem(oldKey);
            logger.debug('Removed old level key', { oldKey });
          } catch (e) {
            logger.warn('Failed to remove old level key', e);
          }
        }
        // Remove daily set for current level (to force regeneration with new goal)
        const currentKey = `dailySet:${today}:${newLevel}`;
        try {
          const keyExistedBefore = localStorage.getItem(currentKey) !== null;
          localStorage.removeItem(currentKey);
          const keyExistsAfter = localStorage.getItem(currentKey) !== null;
          logger.debug('Removed current level key', {
            currentKey,
            keyExistedBefore,
            keyExistsAfter
          });
        } catch (e) {
          logger.warn('Failed to remove current level key', e);
        }
        
        if (generateDailySet) {
          logger.debug('Calling generateDailySet with mode="auto"');
          const dailySetLengthBefore = appState.dailySet.length;
          
          // Clear the current daily set to force regeneration
          // This ensures generateDailySet creates a fresh set even if localStorage removal failed
          appState.dailySet = [];
          
          generateDailySet('auto');
          const dailySetLengthAfter = appState.dailySet.length;
          logger.debug('generateDailySet completed', {
            dailySetLengthBefore,
            dailySetLengthAfter,
            newDailyGoal
          });
        } else {
          logger.warn('generateDailySet callback not available');
        }
      } else {
        logger.warn('getTodayDate callback not available');
      }
    }
    
    // Update UI language if changed
    const languageChanged = newLang !== i18nService.getCurrentLanguage();
    if (languageChanged) {
      logger.debug('Language changed - reloading UI strings');
      i18nService.setLanguage(newLang);
      loadUIStrings(newLang, (): void => {
        if (updateFooter) {
          updateFooter();
        }
        
        // Update header labels with new language
        const settingsLabel = document.getElementById('settings-label');
        if (settingsLabel) {
          settingsLabel.textContent = i18nService.getString('settings') || 'Settings';
        }
        
        // Update account label if not signed in
        const user = authService.getCurrentUser();
        const accountLabel = document.getElementById('account-label');
        if (accountLabel && !user) {
          accountLabel.textContent = i18nService.getString('signIn') || 'Sign In';
        }
        
        // Update library button if it exists
        const libraryBtn = document.getElementById('headerLibraryBtn');
        if (libraryBtn) {
          const libraryLabel = libraryBtn.querySelector('.account-label');
          if (libraryLabel) {
            libraryLabel.textContent = i18nService.getString('library') || 'Library';
          }
        }
        
        // Re-render views after language strings are loaded
        logger.debug('Re-rendering current view after language change');
        reRenderCurrentView();
      });
    } else if (levelChanged || dailyGoalChanged || learningLangChanged) {
      // No language change, but daily set was regenerated - re-render views immediately
      logger.debug('Re-rendering current view after settings change', {
        currentMode: appState.mode,
        dailySetLength: appState.dailySet.length
      });
      reRenderCurrentView();
    }
    
    // Sync to server if signed in
    if (authService.getCurrentUser()) {
      authService.syncPreferences();
    }
    
    closeSettingsModal();
  });
  footer.appendChild(saveBtn);
  
  panel.appendChild(footer);
  
  overlay.appendChild(panel);
  modalContainer.appendChild(overlay);
  
  // Focus the first input
  setTimeout((): void => {
    langSelect.focus();
  }, 50);
  
  // ESC key to close
  const handleEsc = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      closeSettingsModal();
      document.removeEventListener('keydown', handleEsc);
    }
  };
  document.addEventListener('keydown', handleEsc);
  
  // Store handler reference for cleanup
  (overlay as ModalOverlay).escHandler = handleEsc;
  
  // Click outside to close
  overlay.addEventListener('click', (e: MouseEvent): void => {
    if (e.target === overlay) {
      closeSettingsModal();
    }
  });
}
